import {
	buildApiUrl,
	buildStaticPaymentQueryParams,
	getPersonalizedPaymentParams as buildDynamicPaymentQueryParams,
	resolveZip,
	BOA_DEALER_ID,
} from '@atc/wallet-payment-fns';
import {
	getPricingDetails,
	isNewListingType,
	ListingType,
	resolveEcommerce,
	Tenant,
	toIntOrUndef
} from '../../helpers/index.js';
import { getHighAndLowPriceValues } from '../../helpers/pricing.js';
import { resolveDealerZip } from '../../helpers/deriveVehicleAttributes.js';


// Assemble the inputs to GET {host}/rest/retailing/payments. Ports the minimum
// subset of unified-wallet packages/wallet-manager/.../buildCalculatorConfig.js
// needed to make the initial-calc request.

const PAYMENTS_HOST_STAGING = 'https://test.autotrader.com';
const PAYMENTS_HOST_PROD = 'https://www.autotrader.com';
const PAYMENTS_PATH = '/rest/retailing/payments';

export const getPaymentsHost = (useStaging) =>
	(useStaging ? PAYMENTS_HOST_STAGING : PAYMENTS_HOST_PROD);

export const buildPaymentsUrl = (host, path, queryParams) => {
	const base = `${host}${path}`;
	return queryParams ? `${base}?${queryParams}` : base;
};

const isCertified = (vehicle) => {
	const certified = vehicle?.certified ?? vehicle?.certifiedUsed;
	if (typeof certified === 'boolean') {
		return certified;
	}
	if (typeof certified === 'string') {
		return certified.toLowerCase() === 'true';
	}
	return false;
};

export const pickListingType = (vehicle) =>
	(isNewListingType(vehicle?.inventoryType) ? ListingType.New : ListingType.Used);

const pickDealerId = (vehicle, dealerData) =>
	toIntOrUndef(vehicle?.dealerCodes?.mmd)
	?? toIntOrUndef(dealerData?.dealershipCodes?.mmd)
	?? null;

export const resolveVehicleSalePrice = (vehicle) => {
	const finalPrice = toIntOrUndef(vehicle?.finalPrice);
	if (finalPrice > 0) {
		return finalPrice;
	}

	const salePrice = toIntOrUndef(vehicle?.salePrice ?? vehicle?.price);
	if (salePrice > 0) {
		return salePrice;
	}

	return toIntOrUndef(vehicle?.msrp);
};

export const resolveFinalPrices = (vehicle, pricingDetails, listingType) => {
	const defaultMsrp = pricingDetails?.msrp || toIntOrUndef(vehicle?.msrp);
	const defaultSalePrice = pricingDetails?.salePrice || resolveVehicleSalePrice(vehicle);

	const { finalMsrp, finalSalePrice } = getHighAndLowPriceValues(
		defaultMsrp,
		defaultSalePrice,
		toIntOrUndef(vehicle?.salePrice),
		toIntOrUndef(vehicle?.internetPrice),
		toIntOrUndef(vehicle?.askingPrice),
		vehicle?.retailValue,
		listingType,
	);

	const msrp = finalMsrp ?? defaultMsrp;
	const salePrice = finalSalePrice ?? defaultSalePrice;

	return {
		msrp,
		salePrice: salePrice || msrp,
	};
};

// Whether a vehicle has any resolvable price, using the same msrp/salePrice
// resolution buildCalcConfig sends to the payments API — lets callers skip
// the API call entirely (and short-circuit before calling buildCalcConfig)
// when neither price is available.
export const hasPrice = (vehicle, pricingData) => {
	const pricingDetails = getPricingDetails(pricingData ?? {});
	const listingType = pickListingType(vehicle);
	const { msrp, salePrice } = resolveFinalPrices(vehicle, pricingDetails, listingType);
	return Boolean(salePrice || msrp);
};

/**
 * Build the calc config used for the initial payments API call.
 *
 * @param {Object} args
 * @param {Object} args.vehicle - From window.DDC.dataLayer.vehicles[0] / DDC.InvData
 * @param {Object} args.dealerData - From getDealerData()
 * @param {Object} args.dealerSettings - DR Admin config (root or products) — drives hasLeasing/disableIncentives
 * @param {Object} args.defaults - From resolveDefaults: { downPayment, downPaymentPercent, creditTier, ... }
 * @param {Object} args.config - mywallet integration config (uses `useStaging`, optional `partnerId`)
 * @returns {{ paymentUrl: string, configInfo: Object }}
 */
export const buildCalcConfig = ({
	vehicle = {},
	dealerData = {},
	dealerSettings = {},
	defaults = {},
	config = {},
	pricingDetails = {},
} = {}) => {
	const {
		vin,
		trim,
		uuid,
		odometer: rawOdometer,
		mileage: rawMileage,
		chromeId: rawChromeId,
		styleId: rawStyleId,
	} = vehicle;

	const {
		partnerId: dealerPartnerId,
	} = dealerSettings || {};
	const {
		userPostalCode,
		walletZip,
		creditTier,
		loanTerm,
		leaseTerm,
		milesPerYear,
		downPayment,
		downPaymentPercent,
		leaseDownPayment,
		tradeIn,
		financeTermOptions,
		leaseTermOptions,
		supportedDealTypes,
		creditLowScore,
	} = defaults;

	const host = getPaymentsHost(!!config.useStaging);
	const listingType = pickListingType(vehicle);
	const { msrp, salePrice } = resolveFinalPrices(vehicle, pricingDetails, listingType);
	const mileage = toIntOrUndef(rawOdometer ?? rawMileage);
	const chromeStyleId = toIntOrUndef(rawChromeId ?? rawStyleId);
	const dealerZip = resolveDealerZip(vehicle);
	const shopperZip = userPostalCode || dealerZip;
	const zip = resolveZip({ walletZip, shopperZip, dealerZip });
	const isLeaseEnabled = supportedDealTypes?.includes('lease');
	const ecommerce = resolveEcommerce(config.sponsor);
	const isAccelerateOrBOA = ecommerce === Tenant.Accelerate || ecommerce === Tenant.Boa;
	const isBOA = ecommerce === Tenant.Boa;
	const disableIncentives = !isAccelerateOrBOA;
	const dealerId = isBOA ? BOA_DEALER_ID : pickDealerId(vehicle, dealerData);

	const { boaDealerID, partnerId } = isBOA
		? {
			boaDealerID: BOA_DEALER_ID,
			partnerId: Tenant.Boa,
		}
		: {
			boaDealerID: undefined,
			partnerId: (config.partnerId ?? dealerPartnerId ?? undefined),
		};

	const paymentsInfo = {
		loanTerm,
		financeTermOptions,
		creditType: creditTier,
		downPayment,
		downPaymentPercent,
		...(isLeaseEnabled && {
			leaseTerm,
			leaseTermOptions,
			milesPerYear,
			leaseDownPayment,
		}),
		tradeIn,
		includeCash: true,
	};

	const paramsConfig = {
		dealerZip,
		ecommerce,
		msrp,
		dealerId,
		boaDealerID,
		partnerId,
		listingPrice: salePrice,
		disableIncentives,
		chromeStyleId,
		shopperZip,
		supportedDealTypes,
		showRatesTable: !isBOA,
		includeDealerAndLenderFees: isAccelerateOrBOA,
	};

	const listing = {
		mileage,
		vin,
		trim,
		isCertified: isCertified(vehicle),
	};

	const staticQueryParams = buildStaticPaymentQueryParams({ config: paramsConfig, listing, dealerSettings, vehicleType: listingType });
	const dynamicQueryParams = buildDynamicPaymentQueryParams({ paymentsInfo, userZip: zip, creditLowScore });
	const queryParams = buildApiUrl(staticQueryParams, dynamicQueryParams);

	const configInfo = {
		creditTier,
		listingType,
		disableIncentives,
		supportedDealTypes,
		dealerId,
		vin,
		uuid: uuid ?? null,
		salePrice,
		msrp,
	};

	return {
		paymentUrl: buildPaymentsUrl(host, PAYMENTS_PATH, queryParams ),
		configInfo,
	};
};

export default buildCalcConfig;
